/**
 * A porta entre a tela de custos e a API do módulo 11.
 */
import { pedir } from '@/lib/pedido';

export type Criterio = 'medio_ponderado' | 'peps' | 'ultima_compra';

export const ROTULO_DO_CRITERIO: Readonly<Record<Criterio, string>> = {
  medio_ponderado: 'Custo médio ponderado',
  peps: 'PEPS — primeiro que entra, primeiro que sai',
  ultima_compra: 'Preço da última compra',
};

/**
 * O critério que o servidor manda ("ultima_compra") escrito para quem lê. A
 * visão do gestor mostrava o código cru (prova do marco, 07/10/2026).
 */
export function rotuloDoCriterio(codigo: string): string {
  return Object.hasOwn(ROTULO_DO_CRITERIO, codigo) ? ROTULO_DO_CRITERIO[codigo as Criterio] : 'critério não informado';
}

export interface CustoDoEquipamento {
  readonly equipamentoId: string;
  readonly codigo: string;
  readonly descricao: string;
  readonly combustivel: number;
  readonly estoque: number;
  readonly terceiros: number;
  /**
   * A mão de obra pelo valor-hora da função vigente no dia (0095): a da O.S.
   * e a do turno do operador. Já está no `total`. Ausente em servidor anterior.
   */
  readonly maoDeObra?: number;
  readonly total: number;
  /** Quantos lançamentos do período ficaram sem preço conhecido. */
  readonly semPreco: number;
  /** As horas da O.S., valoradas ou não. */
  readonly horasMaoDeObra: number;
  /** As horas de pessoa sem valor, FORA do total (sem pessoa, sem função ou sem valor-hora). */
  readonly horasSemValor?: number;
  /** A frase do servidor sobre essas horas; nula quando toda hora foi valorada. */
  readonly maoDeObraSemValor?: string | null;
  readonly horasMotor: number;
  /** O denominador do custo por hora (guia 9.1, decisão 50). */
  readonly horasProducao: number;
  readonly custoPorHora: number | null;
  /** O custo por hora de motor, como contexto. */
  readonly custoPorHoraMotor: number | null;
  /* ---- 0096 · ao lado do direto (ausentes em servidor anterior) */
  /** A depreciação linear do período. */
  readonly depreciacao?: number;
  /** Sem dado de depreciação em algum dia: a depreciação desta máquina está FORA, e não zero. */
  readonly semDepreciacao?: boolean;
  readonly totalmenteDepreciada?: boolean;
  /** O custo indireto rateado à máquina. */
  readonly indiretoRateado?: number;
  /** Direto + depreciação + indireto rateado: o custo que a margem usa. */
  readonly custoTotal?: number;
  readonly custoTotalPorHora?: number | null;
}

export type CriterioDeRateio = 'horas_produtivas' | 'valor_aquisicao' | 'igual';

export const ROTULO_DO_RATEIO: Readonly<Record<CriterioDeRateio, string>> = {
  horas_produtivas: 'Horas produtivas de cada máquina no mês',
  valor_aquisicao: 'Valor de aquisição de cada máquina',
  igual: 'Partes iguais entre as máquinas ativas',
};

/** O custo indireto do período, como o servidor apurou (0096). */
export interface IndiretoDoPeriodo {
  readonly lancado: number;
  readonly rateado: number;
  readonly semBase: number;
  readonly mesesSemLancamento: readonly string[];
}

export interface CustosDoPeriodo {
  readonly de: string;
  readonly ate: string;
  readonly criterio: Criterio;
  readonly denominador: string;
  /** A frase da hora de pessoa sem valor no período inteiro (0095); nula quando não há. */
  readonly maoDeObraSemValor?: string | null;
  /* ---- 0096 · o custo total e o que ficou fora dele */
  readonly criterioDeRateio?: CriterioDeRateio;
  readonly indireto?: IndiretoDoPeriodo;
  /** O que o custo total inclui, escrito pelo servidor. */
  readonly inclui?: readonly string[];
  /** O que ficou FORA do custo total, escrito pelo servidor (as frases únicas). */
  readonly ausencias?: readonly string[];
  /** Verdadeiro só com `ausencias` vazia. */
  readonly completo?: boolean;
  readonly equipamentos: readonly CustoDoEquipamento[];
  /**
   * Os quatro totais do periodo anterior, de mesma duracao, para a variacao
   * dos cartoes (R02, p.31). Vem como TOTAIS: quem diz que subir e piorar e a
   * tela, nao o servidor.
   */
  readonly anterior: {
    readonly de: string;
    readonly ate: string;
    readonly total: number;
    readonly combustivel: number;
    readonly estoque: number;
    readonly terceiros: number;
    readonly maoDeObra?: number;
    readonly horasSemValor?: number;
    readonly custoTotal?: number;
  };
}

/* ------------------------------------------------ 0096 · custo indireto */

export interface CentroDeCusto {
  readonly id: string;
  readonly codigo: string;
  readonly nome: string;
  readonly ativo: boolean;
}

/** Um lançamento do livro do custo indireto. O estornado continua na lista, marcado. */
export interface LancamentoIndireto {
  readonly id: string;
  /** `aaaa-mm-01`. */
  readonly competencia: string;
  readonly valor: number;
  readonly descricao: string;
  readonly centroCustoId: string;
  readonly centroCodigo: string;
  readonly centroNome: string;
  readonly registradoPor: string | null;
  readonly registradoEm: string;
  readonly motivoDoEstorno: string | null;
  readonly estornadoEm: string | null;
  readonly estornadoPor: string | null;
}

export interface NovoIndireto {
  readonly centroCustoId: string;
  readonly competencia: string;
  readonly valor: number;
  readonly descricao: string;
}

/* ------------------------------------------------ 0096 · depreciação */

export interface VigenciaDaDepreciacao {
  readonly id: string;
  readonly desde: string;
  readonly ate: string | null;
  /** O dia em que a máquina começa a depreciar. */
  readonly inicio: string;
  readonly valorAquisicao: number;
  readonly valorResidual: number;
  readonly vidaUtilMeses: number;
  readonly parcelaMensal: number;
  readonly atualizadoPor: string | null;
}

export interface DepreciacaoDaMaquina {
  readonly equipamentoId: string;
  readonly codigo: string;
  readonly descricao: string;
  /** O parâmetro de hoje; nulo é a lacuna que deixa a margem parcial. */
  readonly vigente: VigenciaDaDepreciacao | null;
  readonly historico: readonly VigenciaDaDepreciacao[];
}

export interface NovaDepreciacao {
  readonly desde: string;
  readonly inicio: string;
  readonly valorAquisicao: number;
  readonly valorResidual: number;
  readonly vidaUtilMeses: number;
  readonly motivo: string;
}

/** Uma vigência do valor-hora de uma função. */
export interface VigenciaDoValorHora {
  readonly id: string;
  /** `aaaa-mm-dd`, inclusive. */
  readonly desde: string;
  /** `aaaa-mm-dd`, exclusivo; nulo quando vale até hoje. */
  readonly ate: string | null;
  readonly valorHora: number;
  readonly atualizadoPor: string | null;
}

/** A função com o valor-hora de hoje e o histórico. O valor é da função, nunca da pessoa. */
export interface FuncaoComValorHora {
  readonly id: string;
  readonly codigo: string;
  readonly nome: string;
  readonly ativo: boolean;
  /** Quantas pessoas exercem a função hoje. */
  readonly pessoas: number;
  readonly valorHoraVigente: number | null;
  readonly vigenteDesde: string | null;
  readonly historico: readonly VigenciaDoValorHora[];
}

export interface NovoValorHora {
  readonly funcaoId: string;
  readonly desde: string;
  readonly valorHora: number;
  readonly motivo: string;
}

export const api = {
  equipamentos: (de: string, ate: string) =>
    pedir<CustosDoPeriodo>(`/custos/equipamentos?de=${encodeURIComponent(de)}&ate=${encodeURIComponent(ate)}`),
  configurar: (criterio: Criterio, motivo: string) =>
    pedir<{ criterioCusteioCombustivel: Criterio }>('/custos/configuracao', {
      method: 'PUT',
      body: JSON.stringify({ criterioCusteioCombustivel: criterio, motivo }),
    }),
  configuracao: () =>
    pedir<{ criterioCusteioCombustivel: Criterio; criterioRateioIndireto?: CriterioDeRateio }>('/custos/configuracao'),
  configurarRateio: (criterio: CriterioDeRateio, motivo: string) =>
    pedir<{ criterioRateioIndireto: CriterioDeRateio; antes: CriterioDeRateio }>('/custos/configuracao/rateio', {
      method: 'PUT',
      body: JSON.stringify({ criterioRateioIndireto: criterio, motivo }),
    }),
  indireto: (de: string, ate: string) =>
    pedir<{ de: string; ate: string; lancamentos: readonly LancamentoIndireto[]; centros: readonly CentroDeCusto[] }>(
      `/custos/indireto?de=${encodeURIComponent(de)}&ate=${encodeURIComponent(ate)}`,
    ),
  lancarIndireto: (corpo: NovoIndireto) =>
    pedir<{ id: string; competencia: string; valor: number }>('/custos/indireto', { method: 'POST', body: JSON.stringify(corpo) }),
  estornarIndireto: (id: string, motivo: string) =>
    pedir<{ id: string }>(`/custos/indireto/${encodeURIComponent(id)}/estorno`, { method: 'POST', body: JSON.stringify({ motivo }) }),
  depreciacao: () => pedir<{ maquinas: readonly DepreciacaoDaMaquina[] }>('/custos/depreciacao'),
  definirDepreciacao: (equipamentoId: string, corpo: NovaDepreciacao) =>
    pedir<{ id: string; equipamentoId: string; desde: string; parcelaMensal: number }>(
      `/custos/depreciacao/${encodeURIComponent(equipamentoId)}`,
      { method: 'POST', body: JSON.stringify(corpo) },
    ),
  valorHora: () => pedir<{ funcoes: readonly FuncaoComValorHora[] }>('/custos/valor-hora'),
  definirValorHora: (corpo: NovoValorHora) =>
    pedir<{ id: string; funcaoId: string; desde: string; valorHora: number }>('/custos/valor-hora', {
      method: 'POST',
      body: JSON.stringify(corpo),
    }),
};
